/**
 * Turns what the Add client form holds into the body the create endpoint
 * takes. The mirror of `clientMapping`, which goes the other way.
 *
 * The work here is all in the gap between the two: the form holds the *names*
 * its dropdowns display, and the API takes the *ids* its catalog lists carry.
 * Resolving one to the other needs the lists themselves, which is why this
 * takes the lookups rather than the client alone.
 */

import { formatEnumLabel } from "./clientMapping";
import { habitValue } from "../preconsultation/preconsultationSteps";
import { toAmountDue } from "../../lib/api";
import { toInternationalNumber } from "../../lib/fieldFormat";
import type { ClientLookup, ClientLookups } from "./hooks/useClientLookups";
import type {
  ClientPreconsultationPayload,
  LookupOption,
  ClientRecord,
  CreateClientPayload,
  NewClient,
} from "./types/clients.types";

/**
 * The form's DD/MM/YYYY into the API's `YYYY-MM-DD`. Only reaches here after
 * `validateNewClient` has passed, so the shape is already known good — the
 * guard is for a caller that skips it, not for a user's typing.
 */
export const toIsoDate = (dob: string): string => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((dob ?? "").trim());
  if (!match) return "";

  const [, day, month, year] = match;
  return `${year}-${month}-${day}`;
};

/**
 * "Male" into the `MALE` the API stores. The mirror of `formatEnumLabel`, and
 * the reason the form can keep displaying labels while the wire keeps
 * constants: "Non binary" goes up as `NON_BINARY`.
 */
export const toEnumConstant = (label: string): string =>
  (label ?? "").trim().toUpperCase().replace(/\s+/g, "_");

/**
 * The API's `YYYY-MM-DD` back into the DD/MM/YYYY the form is typed in — the
 * mirror of `toIsoDate`, and what fills the DOB field when the edit form
 * opens. A client with no date of birth opens on an empty field.
 */
export const toFormDate = (isoDate: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((isoDate ?? "").trim());
  if (!match) return "";

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

/**
 * A stored client back into the answers the form holds — what the edit page
 * opens on.
 *
 * The reference fields are kept whole on a record, so each is unwrapped to the
 * name its dropdown displays; the id behind it is looked up again on the way
 * out, which is what keeps a list edited in between from being saved back with
 * an id it no longer has. Nothing is defaulted or invented: a field the
 * backend has nothing for opens empty, and the form's own validation decides
 * whether that can be saved.
 */
export const toClientForm = (record: ClientRecord): NewClient => ({
  title: record.title?.name ?? "",
  firstName: record.firstName,
  lastName: record.lastName,
  // An enum constant on the wire — the dropdown offers "Male", not `MALE`.
  gender: formatEnumLabel(record.gender),
  dob: toFormDate(record.dateOfBirth),
  profession: record.profession,
  lifestyle: record.lifestyle?.name ?? "",
  address: record.fullAddress,
  // The API's `place`, already mapped to the state by `clientsService`.
  state: record.state,
  city: record.city,
  pincode: record.postalCode,
  phone: record.phone,
  whatsapp: record.whatsapp,
  // Ticked when the two already match, which is what the box means — and what
  // keeps them moving together if the phone is edited from here.
  whatsappSameAsPhone: !!record.phone && record.phone === record.whatsapp,
  email: record.email,
  goals: record.goals.map((goal) => goal.name),
  foodPreference: record.dietaryPattern?.name ?? "",
  foodAllergies: record.foodAllergies.map((allergy) => allergy.name),
  // Empty, always: the subscription group records a purchase being made now,
  // and an edit is not a purchase. The screen that opens on this does not ask
  // for it either — a client's later subscriptions are raised on the Payments
  // tab, where they can be discounted, cancelled and receipted.
  planId: "",
  paidPrice: "",
  purchasedDate: "",

  // The optional half, opened on exactly what is on record — unlike the
  // subscription above, an edit is where these are filled in, whether by the
  // practitioner now or by the client on the pre-consultation form earlier.
  height: record.other.height,
  heightUnit: record.other.heightUnit,
  weight: record.other.weight,
  weightUnit: record.other.weightUnit,
  targetWeight: record.other.targetWeight,
  targetWeightUnit: record.other.targetWeightUnit,
  cuisines: record.other.cuisines,
  dishesLoved: record.other.dishesLoved,
  dishesAvoided: record.other.dishesAvoided,
  // Named on the record and named on the form; the ids behind them are
  // resolved on the way out, against the catalog the field picks from. A name
  // the catalog no longer holds opens as a chip that can be taken off, the
  // same as a goal or an allergy that has been retired.
  conditions: record.other.conditions,
  // `null` on the record is a question nobody has answered, which is the
  // form's own "" — not a No.
  onMedication:
    record.other.onMedication === null
      ? ""
      : record.other.onMedication
        ? "Yes"
        : "No",
  medications: record.other.medications,
  workFrom: record.other.workStart,
  workTo: record.other.workEnd,
  // The client's own answer, now that the wire tells "no fixed hours" apart
  // from a question nobody reached. The box used to open unticked whatever
  // they had said, which quietly turned their answer into a blank on the next
  // save.
  workAnyHours: record.other.noFixedWorkHours,
  sleepFrom: record.other.sleepStart,
  sleepTo: record.other.sleepEnd,
  sleepAnyHours: record.other.noFixedSleep,
  // Read through the choice lists rather than straight off the record: these
  // three held free text before the backend fixed its choices, and a form
  // opened on a word no longer on one of the lists would send it back to a
  // column that now refuses it. See `habitValue`, and what it does with a
  // value it cannot place.
  travel: habitValue("travel", record.other.travel),
  alcohol: habitValue("alcohol", record.other.alcohol),
  smoking: habitValue("smoking", record.other.smoking),
});

/** The id behind a displayed name, or `null` when the list has no such row. */
/**
 * The id behind a displayed name, whichever kind of id its list carries — a
 * number for the backend's catalogs, a `public_id` string for the services the
 * Goal field picks from. What comes back goes on the wire as it is.
 */
const idOf = (
  lookup: ClientLookup,
  name: string
): LookupOption["id"] | null =>
  lookup.options.find((option) => option.name === name)?.id ?? null;

/**
 * Either a body ready to post, or the fields whose names could not be matched
 * to a row in their list.
 *
 * That second case should not happen — the dropdowns only offer names that
 * came from the lists — but it becomes reachable the moment a list is edited
 * on the backend while the form sits open. Better a named failure the screen
 * can report than an id silently sent as `null`.
 */
export type PayloadResult =
  | { ok: true; payload: CreateClientPayload }
  | { ok: false; unresolved: string[] };

/**
 * The reference fields that hold one answer, each with the list it resolves
 * against. Goals and allergies are not here — they hold several, and are
 * resolved as lists of their own below.
 */
const SINGLE_REFERENCE_FIELDS = [
  { label: "Life style", key: "lifeStyles" },
] as const;

/**
 * A reference field the form no longer insists on — the title and the food
 * preference — read as one of two different things.
 *
 * The check `SINGLE_REFERENCE_FIELDS` performs is "a name was picked and its
 * row has gone", which is a real failure and worth refusing a save over. A
 * field left blank is not that; it is now the ordinary case, and putting it
 * through the same test would report every client saved without a title as a
 * catalog that had shifted under the open form.
 *
 * So the two are told apart. Blank means leave the key off the body entirely;
 * picked-but-unresolvable still fails the build, exactly as a life style does.
 */
type OptionalReference = { omit: true } | { omit: false; id: number | null };

const optionalIdOf = (lookup: ClientLookup, name: string): OptionalReference => {
  if (!name.trim()) return { omit: true };
  const id = idOf(lookup, name);
  return { omit: false, id: typeof id === "number" ? id : null };
};

/**
 * The three subscription fields, or nothing at all.
 *
 * They travel together: the backend reads them as one plan sold, and two of
 * them describes a sale nobody can be invoiced for. A group half filled in is
 * refused by `subscriptionErrors` before a save gets this far, so what reaches
 * here is either whole or empty — and empty means the keys are left off the
 * body rather than sent as nulls.
 */
const toSubscriptionFields = (
  client: NewClient
): Pick<CreateClientPayload, "package" | "amount_due" | "added_date"> => {
  const planId = client.planId.trim();
  const paidPrice = client.paidPrice.trim();
  const purchasedDate = client.purchasedDate.trim();

  if (!planId || !paidPrice || !purchasedDate) return {};

  return {
    // Under `package`, which is what the backend files a plan as - the plan
    // dropdown holds the `public_id` this sends.
    package: planId,
    // Rupees as the backend keeps them: `"400.00"`, not `400`.
    amount_due: toAmountDue(Number(paidPrice)),
    added_date: toIsoDate(purchasedDate),
  };
};

/** `09:00` as the `09:00:00` the endpoint takes. */
const timeOf = (value: string): string => `${value.trim()}:00`;

/**
 * What the block carries that the accordion did not answer for itself.
 *
 * The first three are echoes: the lifestyle, the food preference and the goals
 * are asked at the top of the client form and repeated here, because the
 * pre-consultation form the backend raises holds its own copy of each. The
 * conditions are not — the accordion does ask for them, but as names, and only
 * the caller holds the catalog to resolve those against.
 */
interface PreconsultationExtras {
  life_style: number;
  /** Absent where the client form was saved without a food preference. */
  dietary_pattern?: number;
  /** Service `public_id`s — the same values the client's `goals` carries. */
  goal_ids: string[];
  /** Catalog ids, and absent where no condition was picked. */
  medical_condition_ids?: number[];
  /** The same, for the cuisines. Absent where none was picked. */
  favorite_cuisine_ids?: number[];
}

/**
 * The `pre_consultation` block, as far as the accordion was filled in — and
 * `undefined` where none of it was.
 *
 * Only what has something in it. A field sent empty is a field cleared, and
 * this body is `PATCH`ed on the edit path — so a practitioner who opens the
 * form to correct a phone number must not thereby wipe the answers their
 * client gave on the pre-consultation form. What was answered and then
 * deliberately emptied is the one case this cannot express, and it is the
 * right way round: losing an edit is recoverable, losing a client's own
 * answers is not.
 *

 * The units ride along only with the figure they belong to — a unit on its own
 * describes nothing.
 */
const toPreconsultationBlock = (
  client: NewClient,
  extras: PreconsultationExtras
): ClientPreconsultationPayload | undefined => {
  const answers: ClientPreconsultationPayload = {};

  /** `"165.00"` — a measurement as the decimal string the backend keeps. */
  const measure = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const amount = Number(trimmed);
    return Number.isFinite(amount) ? amount.toFixed(2) : undefined;
  };

  const height = measure(client.height);
  if (height) {
    answers.height_value = height;
    // `CM`, not the `cm` the field shows — the form's own casing on the wire.
    answers.height_unit = client.heightUnit.toUpperCase();
  }

  const weight = measure(client.weight);
  if (weight) {
    answers.weight_value = weight;
    answers.weight_unit = client.weightUnit.toUpperCase();
  }

  const targetWeight = measure(client.targetWeight);
  if (targetWeight) {
    answers.target_weight_value = targetWeight;
    answers.target_weight_unit = client.targetWeightUnit.toUpperCase();
  }

  // An answer like any other, rather than one of the echoes below: a client
  // form filled in no further than this still raises the form the conditions
  // were recorded on.
  if (extras.medical_condition_ids?.length) {
    answers.medical_condition_ids = extras.medical_condition_ids;
  }
  if (extras.favorite_cuisine_ids?.length) {
    answers.favorite_cuisine_ids = extras.favorite_cuisine_ids;
  }

  if (client.dishesLoved.length > 0) {
    answers.favorite_dishes = client.dishesLoved;
  }
  if (client.dishesAvoided.length > 0) {
    answers.foods_to_avoid = client.dishesAvoided;
  }

  // The hours go up as a pair or not at all — half a range is not a day. A
  // ticked "No fixed hours" sends `null` for both with the flag beside them:
  // there are no hours, rather than nobody got that far.
  if (client.workAnyHours) {
    answers.working_hours_start = null;
    answers.working_hours_end = null;
    answers.has_no_fixed_working_hours = true;
  } else if (client.workFrom && client.workTo) {
    answers.working_hours_start = timeOf(client.workFrom);
    answers.working_hours_end = timeOf(client.workTo);
  }

  if (client.sleepAnyHours) {
    answers.sleep_start = null;
    answers.sleep_end = null;
    answers.has_no_fixed_sleep_schedule = true;
  } else if (client.sleepFrom && client.sleepTo) {
    answers.sleep_start = timeOf(client.sleepFrom);
    answers.sleep_end = timeOf(client.sleepTo);
  }

  if (client.travel.trim()) answers.travel_frequency = client.travel.trim();
  if (client.alcohol.trim()) answers.alcohol_frequency = client.alcohol.trim();
  if (client.smoking.trim()) answers.smoking_frequency = client.smoking.trim();

  if (client.onMedication) {
    answers.takes_medications = client.onMedication === "Yes";
    // The names only where there are said to be some. A list under a No is a
    // contradiction, and the form already clears it — this is the second line.
    if (client.onMedication === "Yes" && client.medications.length > 0) {
      answers.medications = client.medications;
    }
  }

  // Nothing was answered, so there is no form to raise. The echoes alone are
  // not a questionnaire — they are what the top of the client form already
  // collected, and a record carrying an approved form nobody filled in would
  // read as a client who had been through one.
  if (Object.keys(answers).length === 0) return undefined;

  const {
    medical_condition_ids: _conditions,
    favorite_cuisine_ids: _cuisines,
    ...echoes
  } = extras;
  return { state: client.state.trim(), ...echoes, ...answers };
};

export const toCreateClientPayload = (
  client: NewClient,
  lookups: ClientLookups
): PayloadResult => {
  const title = optionalIdOf(lookups.titles, client.title);
  const dietaryPattern = optionalIdOf(
    lookups.dietaryPatterns,
    client.foodPreference
  );
  const ids = {
    lifeStyles: idOf(lookups.lifeStyles, client.lifestyle),
  };

  // The two list fields, each name resolved on its own. One that no longer
  // matches a row fails the whole build rather than being quietly dropped — a
  // client saved without a goal or an allergy they were given is worse than a
  // refused save.
  // The goals resolve against the practice's services, not the goal catalog:
  // the field picks from them, and what goes up is each service's `public_id`.
  const goalIds = client.goals.map((name) => idOf(lookups.serviceGoals, name));
  const allergenIds = client.foodAllergies.map((name) =>
    idOf(lookups.allergens, name)
  );
  // The same bargain, for the same reason: a condition the catalog no longer
  // holds is refused rather than dropped, since a client saved without one
  // they are being treated for is worse than a refused save.
  const conditionIds = client.conditions.map((name) =>
    idOf(lookups.medicalConditions, name)
  );
  const cuisineIds = client.cuisines.map((name) =>
    idOf(lookups.cuisines, name)
  );

  const unresolved = [
    ...(!title.omit && title.id === null ? ["Title"] : []),
    ...SINGLE_REFERENCE_FIELDS.filter(({ key }) => ids[key] === null).map(
      ({ label }) => label
    ),
    ...(!dietaryPattern.omit && dietaryPattern.id === null
      ? ["Food preference"]
      : []),
    ...(goalIds.some((id) => id === null) ? ["Goal"] : []),
    ...(allergenIds.some((id) => id === null) ? ["Food allergies"] : []),
    ...(conditionIds.some((id) => id === null) ? ["Medical conditions"] : []),
    ...(cuisineIds.some((id) => id === null) ? ["Favorite cuisines"] : []),
  ];

  if (unresolved.length > 0) return { ok: false, unresolved };

  const preconsultation = toPreconsultationBlock(client, {
    life_style: ids.lifeStyles as number,
    ...(dietaryPattern.omit
      ? {}
      : { dietary_pattern: dietaryPattern.id as number }),
    goal_ids: goalIds as string[],
    medical_condition_ids: conditionIds as number[],
    favorite_cuisine_ids: cuisineIds as number[],
  });

  return {
    ok: true,
    payload: {
      // Left off the body where it was not picked, rather than sent as a null
      // or a nought. This field takes a catalog id, and both of those are a
      // claim about the person: one that no title fits them, one that some row
      // does. An absent key says neither, which is the truth — and it is the
      // same answer `toSubscriptionFields` below gives to a client who arrived
      // without a plan.
      ...(title.omit ? {} : { title: title.id as number }),
      first_name: client.firstName.trim(),
      last_name: client.lastName.trim(),
      gender: toEnumConstant(client.gender),
      // The same treatment, for the same reason: `toIsoDate` answers an empty
      // field with an empty string, and this column takes a date.
      ...(client.dob.trim() ? { date_of_birth: toIsoDate(client.dob) } : {}),
      // Not a reference list yet — the backend takes the label as typed.
      profession: client.profession,
      life_style: ids.lifeStyles as number,
      full_address: client.address.trim(),
      // `place` is the state, matching what the read endpoints return there.
      place: client.state,
      postal_code: client.pincode,
      city: client.city,
      phone_number: toInternationalNumber(client.phone),
      whatsapp_number: toInternationalNumber(client.whatsapp),
      email: client.email.trim(),
      goals: goalIds as string[],
      // And the third of them — a catalog id like the title, absent on the
      // same terms.
      ...(dietaryPattern.omit
        ? {}
        : { dietary_pattern: dietaryPattern.id as number }),
      food_allergies: allergenIds as number[],
      // The plan is a `public_id` rather than a catalog id, so there is
      // nothing to resolve — the dropdown holds what the endpoint takes.
      ...toSubscriptionFields(client),
      // The optional details, in the one block the endpoint takes them in, and
      // left off entirely where the accordion was never opened.
      ...(preconsultation ? { pre_consultation: preconsultation } : {}),
    },
  };
};
